import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, KeyRound, Eye, EyeOff, Lock, CheckCircle2 } from "lucide-react";

const RULES = [
    { test: (v) => v.length >= 8, label: "At least 8 characters" },
    { test: (v) => /[A-Za-z]/.test(v), label: "Includes a letter" },
    { test: (v) => /\d/.test(v), label: "Includes a number" },
];

/**
 * Forced-first-login change screen + voluntary rotation.
 * `?forced=1` in the URL hides the "back" affordance so users can't bail
 * out until they've picked a new password.
 */
export default function ChangePassword({ forced = false }) {
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [cur, setCur] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const rulesState = RULES.map((r) => ({ ...r, pass: r.test(pw) }));
    const strong = rulesState.every((r) => r.pass);
    const matches = pw && pw === pw2;

    const submit = async (e) => {
        e.preventDefault();
        if (busy || !strong || !matches) return;
        setBusy(true);
        setError("");
        try {
            await api.post("/auth/change-password", { current_password: cur, new_password: pw });
            setDone(true);
            await refresh?.();
            setTimeout(() => nav("/", { replace: true }), 1500);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-5 login-scope">
            <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden text-white p-10 xl:p-14 flex-col login-hero">
                <div className="login-hero__grid" aria-hidden />
                <div className="login-hero__glow" aria-hidden />
                <div className="relative z-10 max-w-2xl mt-auto mb-auto">
                    <div className="login-brandmark" style={{ width: 60, height: 60, borderRadius: 16 }}>
                        <ShieldCheck size={28} strokeWidth={2} />
                    </div>
                    <h1 className="login-hero__title mt-6">
                        {forced ? "Set your own password." : "Rotate your password."}
                    </h1>
                    <p className="login-hero__tagline">
                        {forced
                            ? "First-time sign-in. Pick something only you know."
                            : "Choose a new password — old one is instantly invalidated."}
                    </p>
                    <div className="login-hero__accent-line" />
                    <div className="mt-8 max-w-md text-[13px] text-slate-300 leading-relaxed">
                        Passwords are stored as bcrypt hashes. Even we can&rsquo;t read them.
                        You can rotate again any time from the Security page.
                    </div>
                </div>
            </div>

            <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-10 bg-stone-50">
                <div className="w-full max-w-md bg-white rounded-2xl border border-stone-200/70 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] p-8 sm:p-10 login-card" data-testid="change-password-page">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="login-brandmark">
                            <ShieldCheck size={22} strokeWidth={2} />
                        </div>
                        <div className="leading-tight">
                            <div className="text-[15px] font-bold text-slate-900 tracking-[0.14em] leading-none">
                                CREDIT CARD
                            </div>
                            <div className="text-[13px] font-semibold text-slate-500 tracking-[0.14em] leading-none mt-1.5">
                                CONTROL CENTER
                            </div>
                        </div>
                    </div>

                    {done ? (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="change-password-success">
                            <div className="flex items-start gap-3">
                                <CheckCircle2 size={22} className="text-emerald-600 mt-0.5 shrink-0" />
                                <div>
                                    <div className="text-emerald-900 font-semibold">Password updated.</div>
                                    <div className="text-emerald-800 text-sm mt-1">Taking you to the dashboard…</div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="mb-5">
                                <h2 className="text-[26px] font-semibold text-slate-900 leading-tight">
                                    {forced ? "One-time setup" : "Change password"}
                                </h2>
                                <p className="text-sm text-slate-500 mt-1">
                                    {forced
                                        ? "Set a password only you know before you continue."
                                        : "Your session stays active on this device."}
                                </p>
                            </div>

                            <form onSubmit={submit}>
                                <label className="block mb-3">
                                    <span className="block text-[13px] font-medium text-slate-700 mb-1.5">
                                        Current password
                                    </span>
                                    <span className="login-field">
                                        <KeyRound size={16} className="login-field__icon" strokeWidth={1.9} />
                                        <input
                                            type={showPw ? "text" : "password"}
                                            value={cur}
                                            onChange={(e) => setCur(e.target.value)}
                                            className="login-field__input"
                                            autoComplete="current-password"
                                            data-testid="chpw-current"
                                            required
                                        />
                                        <button type="button" onClick={() => setShowPw((v) => !v)} className="login-field__eye" tabIndex={-1} aria-label="Toggle password">
                                            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </span>
                                </label>

                                <label className="block mb-3">
                                    <span className="block text-[13px] font-medium text-slate-700 mb-1.5">
                                        New password
                                    </span>
                                    <span className="login-field">
                                        <KeyRound size={16} className="login-field__icon" strokeWidth={1.9} />
                                        <input
                                            type={showPw ? "text" : "password"}
                                            value={pw}
                                            onChange={(e) => setPw(e.target.value)}
                                            className="login-field__input"
                                            autoComplete="new-password"
                                            placeholder="At least 8 characters"
                                            data-testid="chpw-new"
                                            required
                                        />
                                    </span>
                                </label>

                                <label className="block mb-4">
                                    <span className="block text-[13px] font-medium text-slate-700 mb-1.5">
                                        Confirm new password
                                    </span>
                                    <span className="login-field">
                                        <KeyRound size={16} className="login-field__icon" strokeWidth={1.9} />
                                        <input
                                            type={showPw ? "text" : "password"}
                                            value={pw2}
                                            onChange={(e) => setPw2(e.target.value)}
                                            className="login-field__input"
                                            autoComplete="new-password"
                                            data-testid="chpw-confirm"
                                            required
                                        />
                                    </span>
                                    {pw2 && !matches && (
                                        <div className="text-xs text-red-600 mt-1.5">Passwords do not match.</div>
                                    )}
                                </label>

                                <div className="mb-5 grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                                    {rulesState.map((r) => (
                                        <div key={r.label} className={`text-[11px] px-2 py-1 rounded-md border flex items-center gap-1 ${r.pass ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-stone-500 border-stone-200 bg-stone-50"}`}>
                                            <CheckCircle2 size={11} strokeWidth={r.pass ? 2.4 : 1.8} />
                                            {r.label}
                                        </div>
                                    ))}
                                </div>

                                {error && (
                                    <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="chpw-error">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={busy || !strong || !matches || !cur}
                                    className="login-signin-btn"
                                    data-testid="chpw-submit"
                                >
                                    <Lock size={16} strokeWidth={2.1} />
                                    {busy ? "Saving…" : "Set new password"}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
